import React, { useState } from 'react';
import { Loader } from 'lucide-react';
import { useLazyQuery, useMutation, gql } from '@apollo/client';
import { toast } from '@/components/ui/toast';
import { AD_CLIENT_ID, AD_TENANT_ID, AD_REDIRECT_URI } from "../../../../settings";

const REQUEST_TWO_FACTOR_BY_TOKEN = gql`
  query RequestTwoFactorByToken($token: String!) {
    requestTwoFactorByToken(token: $token)
  }
`;

const VALIDATE_TWO_FACTOR_BY_TOKEN = gql`
  query ValidateTwoFactorByToken($token: String!, $code: String!) {
    validateTwoFactorByToken(token: $token, code: $code)
  }
`;

const LOGIN_AZURE_AD_EMAIL = gql`
  mutation LoginAzureADEmail($code: String!) {
    loginAzureADEmail(code: $code) {
      token
    }
  }
`;

interface TwoFactorLoginProps {
  token?: string;
  setTwofactorPass: (passed: boolean, newToken: string) => void;
}

export const TwoFactorLogin: React.FC<TwoFactorLoginProps> = ({ token, setTwofactorPass }) => {
  const [twoFactorBoxValue, setTwoFactorBoxValue] = useState<string>('');
  const [verifying, setVerifying] = useState<boolean>(false);
  const [codeRequested, setCodeRequested] = useState<boolean>(false);

  // Check for Azure AD callback on component mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    
    if (code) {
      handleAzureCallback(code);
    }
  }, []);

  const [requestTwoFactorByToken] = useLazyQuery<{ requestTwoFactorByToken: boolean }>(REQUEST_TWO_FACTOR_BY_TOKEN);
  const [validateTwoFactorByToken] = useLazyQuery<{ validateTwoFactorByToken: string }>(VALIDATE_TWO_FACTOR_BY_TOKEN);
  const [loginAzureADEmail] = useMutation<{ loginAzureADEmail: { token: string } }>(LOGIN_AZURE_AD_EMAIL);

  const handleTwoFactorBoxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTwoFactorBoxValue(e.target.value);
  };

  const handleRequestCode = async () => {
    if (!token) return;
    try {
      const { data } = await requestTwoFactorByToken({ variables: { token } });
      if (data?.requestTwoFactorByToken) {
        toast.success('Two-factor code sent to your email');
        setCodeRequested(true);
      }
    } catch (error) {
      toast.error('Failed to request code');
    }
  };

  const handleVerify = async () => {
    if (!token) return;
    setVerifying(true);
    try {
      const { data } = await validateTwoFactorByToken({ variables: { token, code: twoFactorBoxValue } });
      if (data?.validateTwoFactorByToken) {
        setTwofactorPass(true, data.validateTwoFactorByToken);
      } else {
        toast.error('Invalid two-factor code');
        setVerifying(false);
      }
    } catch (error) {
      toast.error('Verification failed');
      setVerifying(false);
    }
  };

  const handleAzureLogin = () => {
    setVerifying(true);
    // Redirect to Azure AD login
    window.location.href = `https://login.microsoftonline.com/${AD_TENANT_ID}/oauth2/v2.0/authorize?
      client_id=${AD_CLIENT_ID}
      &response_type=code
      &redirect_uri=${encodeURIComponent(`${AD_REDIRECT_URI}/mail`)}
      &response_mode=query
      &scope=openid profile email
      &state=email_2fa_bypass_${Math.random().toString(36).substring(2, 15)}`;
  };

  const handleAzureCallback = async (code: string) => {
    setVerifying(true);
    try {
      const { data } = await loginAzureADEmail({ variables: { code } });
      const token = data?.loginAzureADEmail?.token;
      if (token) {
        // Clear URL parameters
        window.history.replaceState({}, document.title, window.location.pathname);
        setTwofactorPass(true, token);
        return;
      }
      toast.error('Azure authentication failed');
      setVerifying(false);
    } catch (error) {
      toast.error('Azure authentication failed');
      setVerifying(false);
    }
  };

  return (
    <div className="flex items-center justify-center pt-12">
      <div className="w-full max-w-md space-y-8 bg-white p-8 rounded-lg shadow">
        {/* Logo */}
        <div className="flex justify-center">
          <img src={`${process.env.PUBLIC_URL}/logoODR.png`} alt="logo" className="h-16 w-auto" />
        </div>

        {/* Title */}
        <h2 className="text-center text-2xl font-bold">Message Center</h2>
        <h3 className="text-center text-xl font-bold">Two-Factor Authentication</h3>

        {/* SSO Login - Primary Option */}
        {!codeRequested && (
          <div className="space-y-4">
            <button 
              onClick={handleAzureLogin}
              disabled={verifying}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-semibold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {verifying ? (
                <Loader className="h-5 w-5 animate-spin" />
              ) : (
                "Continue with Microsoft SSO"
              )}
            </button>
            
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or use email code</span>
              </div>
            </div>
            
            <button
              onClick={handleRequestCode}
              className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            >
              Request Email Code
            </button>
          </div>
        )}

        {/* Verification Form */}
        {codeRequested && (
          <>
            <div className="text-center text-sm text-gray-600">
              A code has been sent to your email address
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Two-Factor Code</label>
              <input
                type="text"
                value={twoFactorBoxValue}
                onChange={handleTwoFactorBoxChange}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Enter code"
              />
            </div>
            <button
              onClick={handleVerify}
              disabled={verifying}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {verifying ? <Loader className="h-5 w-5 animate-spin" /> : 'Verify'}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default TwoFactorLogin;
